import React, {useEffect, useId, useState} from 'react';
import {Autocomplete, Box, InputAdornment, TextField, Tooltip} from '@mui/material';
import {BoltOutlined, Check} from '@mui/icons-material';
import {alpha} from '@mui/material/styles';
import {trafficSourceServices} from '../../service/trafficSourceService';
import {reportingInputStyles, reportingMenuPaperStyles} from './ReportingToolbarDesign';

const label = 'Optimize by traffic source';
const description = 'Choose one traffic source to unlock its reports and additional columns. Connected sources let you adjust daily budgets and bids, and turn campaigns, ad sets, ads or zones on and off.';
const getLabel = option => option.label || '';
const equal = (option, value) => option.value === value.value;

export default function TrafficSourceFilter({inputValue = [], updateOnChange}) {
    const [open, setOpen] = useState(false);
    const [search, setSearch] = useState('');
    const [options, setOptions] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(false);
    const id = useId();
    const selected = inputValue[0] || null;
    useEffect(() => {
        if (!open) return;
        let active = true;
        setLoading(true);
        setError(false);
        const timer = setTimeout(async () => {
            try {
                const response = await trafficSourceServices.read(search);
                const result = response.traffics.map(item => ({
                    ...item, value: item.id, label: item.name,
                    ...Object.fromEntries(Array.from({length: 10}, (_, index) => [`custom${index + 1}`, item[`custom${index + 1}Name`]]))
                }));
                if (active) setOptions(result);
            } catch {
                if (active) {setOptions([]); setError(true);}
            } finally {if (active) setLoading(false);}
        }, search ? 300 : 0);
        return () => {active = false; clearTimeout(timer);};
    }, [open, search]);

    return <Tooltip title={description} describeChild placement="top">
        <Autocomplete id={id} fullWidth open={open} onOpen={() => {setSearch(''); setOpen(true);}} onClose={() => setOpen(false)}
            value={selected} onChange={(event, value) => updateOnChange(value ? [value] : [])}
            options={options} loading={loading} getOptionLabel={getLabel} isOptionEqualToValue={equal}
            onInputChange={(event, value, reason) => {if (reason === 'input' || reason === 'clear') setSearch(value);}}
            noOptionsText={error ? 'Unable to load traffic sources. Try again.' : 'No traffic sources found'}
            slotProps={{paper: {sx: {...reportingMenuPaperStyles, width: 'auto'}}}}
            renderOption={({key, ...props}, option, {selected: active}) => <Box component="li" key={key} {...props}
                sx={{display: 'flex', justifyContent: 'space-between', gap: 1, fontSize: 13}}>
                {getLabel(option)}{active && <Check color="primary" fontSize="small"/>}
            </Box>}
            renderInput={params => <TextField {...params} size="small" label={label} placeholder="Select a traffic source"
                sx={{...reportingInputStyles,
                    '& .MuiOutlinedInput-root': {...reportingInputStyles['& .MuiOutlinedInput-root'],
                        bgcolor: theme => alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.12 : 0.06)},
                    '& .MuiOutlinedInput-notchedOutline': {border: '1px solid', borderColor: theme => alpha(theme.palette.primary.main, selected ? 0.5 : 0.25)},
                    '& .MuiInputLabel-root': {fontSize: 13, fontWeight: 600, color: 'primary.main'}}}
                slotProps={{inputLabel: {shrink: true}, htmlInput: {...params.inputProps, 'aria-label': label},
                    input: {...params.InputProps, startAdornment: <InputAdornment position="start"><BoltOutlined sx={{fontSize: 20, color: 'primary.main'}}/></InputAdornment>}}}/>}/>
    </Tooltip>;
}
